import { useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useAccesControl } from '@/hooks/useAccessControl';
import { useAction } from '@/hooks/useAction';

import { useProfileContext } from '@/features/context/ProfileContext';

import { CardTransactions } from '@/features/cards/components/CardTransactions';

import PaymentCards from '@/features/cards/components/PaymentCards';
import { getCards, setCurrentCardId } from '@/store/actions/profile.actions';

import { selectProfileCardsByUserId } from '@/store/selectors';
import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { featuresSlugs } from '@/features/roles/store/models/features.model';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { CardUnitCreation } from './CardUnitCreation';

export interface ISlugCardTabs {
  cardId: string;
}

export const PaymentCardsTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    user: { userId },
  } = useProfileContext();
  const { cardId } = useParams();

  const { displayActionFromConfig } = useAction();

  const { hasAccessToFeature } = useAccesControl();

  const isLoading = useAppSelector((state) => selectProfileCardsByUserId(state, userId)?.isLoading);
  const error = useAppSelector((state) => selectProfileCardsByUserId(state, userId)?.error);

  const canCreateCard = displayActionFromConfig('createCard') && hasAccessToFeature(featuresSlugs.CREATE_CARDS);

  const hasCards = useAppSelector((state) =>
    Boolean(selectProfileCardsByUserId(state, userId)?.state?.map((cards) => cards.state?.card)?.length),
  );

  const firstPaymentCardId = useAppSelector(
    (state) => selectProfileCardsByUserId(state, userId)?.state?.map((cards) => cards.state?.card)?.[0]?.cardId,
    shallowEqual,
  );

  useEffect(() => {
    dispatch(getCards(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (cardId) dispatch(setCurrentCardId(userId, Number(cardId)));
  }, [dispatch, cardId, userId]);

  useEffect(() => {
    if (hasCards && !cardId) {
      navigate(DashboardHelpers.generateProfilePath(userId, { cardId: firstPaymentCardId }));
    }
  }, [hasCards, cardId, userId, firstPaymentCardId, navigate]);

  if (!hasCards && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px]">
        <EmptyState
          icon="card_issuing"
          size="l"
          title={t('pages.currentProfiles.cards.creation.title')}
          state={canCreateCard ? undefined : t('pages.currentProfiles.cards.creation.description')}
          className="p-0"
        />
        {canCreateCard && <CardUnitCreation />}
      </div>
    );
  }

  if (error && !isLoading)
    return (
      <div className="flex flex-col items-center w-full gap-3 justify-center h-[400px]">
        <h4 className="typo-h4-bold text-error">{t('pages.cores.error')}</h4>
        <p className="typo-body-regular-paragraph text-helper">{DashboardHelpers.getErrorMessage(error)}</p>
      </div>
    );

  return (
    <div className="flex mt-6">
      <div className="overflow-x-hidden overflow-y-scroll h-[calc(100vh-350px)] lightScrollBar pr-6">
        <PaymentCards canCreateCard={canCreateCard} />
      </div>
      <div className="h-auto border-l border-primary mr-6" />
      <CardTransactions />
    </div>
  );
};

export default PaymentCardsTab;
